"use client";

import { Fragment } from "react";
import { vercelStegaSplit } from "@vercel/stega";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { resolveI18nText } from "@/sanity/globals/I18n";
import type { ITopBanner } from "@/sanity/globals/TopBanner";
import type { ITimeLeft } from "@/lib/bannerCountdown";
import { useBannerCountdown } from "@/hooks/useBannerCountdown";

import liveDot from "@/public/top-banner/live-dot.svg";
import liveDotMobile from "@/public/top-banner/live-dot-mobile.svg";

type CountdownLanguage = "de" | "en";

const COUNTDOWN_UNITS: Record<CountdownLanguage, { key: keyof ITimeLeft; short: string; one: string; many: string }[]> =
  {
    de: [
      { key: "days", short: "Tage", one: "Tag", many: "Tage" },
      { key: "hours", short: "Std", one: "Stunde", many: "Stunden" },
      { key: "minutes", short: "Min", one: "Minute", many: "Minuten" },
      { key: "seconds", short: "Sek", one: "Sekunde", many: "Sekunden" },
    ],
    en: [
      { key: "days", short: "Days", one: "day", many: "days" },
      { key: "hours", short: "Hrs", one: "hour", many: "hours" },
      { key: "minutes", short: "Min", one: "minute", many: "minutes" },
      { key: "seconds", short: "Sec", one: "second", many: "seconds" },
    ],
  };

const pad = (n: number) => String(n).padStart(2, "0");

/** "Launch Event [[TradeMania 2.0]]": the bracketed parts are set in semibold. */
function HighlightedText({ text }: { text: string }) {
  // keep the invisible stega payload (visual editing) out of the [[…]] split
  const { cleaned, encoded } = vercelStegaSplit(text);
  return (
    <>
      {cleaned.split(/\[\[(.+?)\]\]/g).map((part, i) =>
        i % 2 === 1 ? (
          <span
            key={i}
            className="font-semibold">
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
      {encoded && <span className="sr-only">{encoded}</span>}
    </>
  );
}

/**
 * Boxed digits with the unit below and colons between the boxes. Phones use the mobile frame's
 * scaled-down sizes (box 29.85 × 26.19, 20px digits), lg up the desktop ones (34 × 32, 22px).
 */
function Countdown({ timeLeft, language }: { timeLeft: ITimeLeft; language: CountdownLanguage }) {
  const units = COUNTDOWN_UNITS[language];
  const label = units.map(({ key, one, many }) => `${timeLeft[key]} ${timeLeft[key] === 1 ? one : many}`).join(", ");

  return (
    <div
      role="timer"
      aria-label={label}
      className="flex items-start gap-[.433rem] lg:gap-[.5rem]">
      {units.map(({ key, short }, i) => (
        <Fragment key={key}>
          {i > 0 && (
            <span
              aria-hidden
              className="font-bebas text-[2.597rem] leading-[2.75rem] lg:text-[3rem] lg:leading-[3.625rem]">
              :
            </span>
          )}
          <span
            aria-hidden
            className="flex flex-col items-center gap-[.325rem] lg:gap-[.375rem]">
            <span className="flex min-w-[3.731rem] items-center justify-center rounded-[.433rem] bg-[#0B0C0D]/[.08] px-[.866rem] py-[.325rem] font-bebas text-[2.5rem] leading-[2.625rem] tabular-nums lg:min-w-[4.25rem] lg:rounded-[.5rem] lg:px-[1rem] lg:py-[.375rem] lg:text-[2.75rem] lg:leading-[3.25rem]">
              {pad(timeLeft[key])}
            </span>
            <span className="text-[1.25rem] leading-[1.625rem] font-medium tracking-[.075rem] text-[#0B0C0D]/[.55] uppercase">
              {short}
            </span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Mint bar above the page. Figma BitBull Website · "Banner · Website": phones (430 frame) show
 * the dot, the two-line phone text and the countdown; lg up (1920 frame) the label, the
 * highlighted text, the countdown and the black launch button in one row.
 */
export default function TopBanner({
  data,
  language,
  renderedAt,
}: {
  data: ITopBanner;
  language?: string;
  /** Server clock when the page was rendered, for a hydration-safe first countdown frame. */
  renderedAt: number;
}) {
  const label = resolveI18nText(data.preText, data.preTextI18n, language);
  const text = resolveI18nText(data.text, data.textI18n, language);
  const mobileText = resolveI18nText(data.mobileText, data.mobileTextI18n, language) || label;
  const timeLeft = useBannerCountdown(data, renderedAt);

  return (
    <div className="w-full bg-[#D9F9B9] px-[2rem] py-[1rem] font-geist text-[#0B0C0D] lg:px-[4rem] lg:py-0">
      {/* only the content animates: a sliding bar would uncover the page background behind it */}
      <div className="fade-in slide-in-from-top-2 mx-auto flex w-full max-w-[180.5rem] animate-in items-center justify-center duration-[600ms] ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:animate-none lg:min-h-[7rem] lg:justify-between lg:gap-[4rem]">
        {/* phones: 240px block, the 224px text wraps onto its second line */}
        {mobileText && (
          <p className="mb-0 flex w-[30rem] shrink-0 items-start gap-[1rem] text-[1.75rem] leading-[2.25rem] font-medium tracking-[-.0175rem] uppercase lg:hidden">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={liveDotMobile.src}
              alt=""
              width={8}
              height={13}
              className="h-[1.625rem] w-[1rem] shrink-0"
            />
            <span className="min-w-0 flex-1">
              <HighlightedText text={mobileText} />
            </span>
          </p>
        )}

        {/* lg up */}
        <div className="hidden min-w-0 items-center gap-[2.25rem] lg:flex">
          {label && (
            <p className="mb-0 flex items-center gap-[1rem] text-[2rem] leading-[2.625rem] font-medium whitespace-nowrap uppercase">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={liveDot.src}
                alt=""
                width={8}
                height={8}
                className="h-[1rem] w-[1rem] shrink-0"
              />
              {label}
            </p>
          )}
          {label && text && (
            <span
              aria-hidden
              className="h-[2.5rem] w-px shrink-0 bg-[#0B0C0D]/[.18]"
            />
          )}
          {text && (
            <p className="mb-0 text-[2rem] leading-[2.625rem] font-medium">
              <HighlightedText text={text} />
            </p>
          )}
        </div>

        {(timeLeft || data.cta) && (
          <div className="flex shrink-0 flex-col items-start gap-[2rem] lg:flex-row">
            {timeLeft && (
              <Countdown
                timeLeft={timeLeft}
                language={language === "en" ? "en" : "de"}
              />
            )}
            {data.cta && (
              <div className="hidden lg:block">
                <CTACornerButton
                  data={{ type: "launchBlack", cta: data.cta }}
                  size="sm"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-[1rem] w-full lg:hidden">
        <CTACornerButton
          data={{ type: "launchBlack", cta: data.cta }}
          size="sm"
          fullWidth
        />
      </div>
    </div>
  );
}
